/**
 * Résolution hub / tenant côté navigateur, à partir du `hostname` courant.
 *
 * Centralise la connaissance des domaines racine pour éviter de dupliquer les
 * suffixes (`.crayea.fr`, `.localhost`, …) dans `Home`, `Handoff` et
 * `AuthShellLayout` — chaque duplication étant un risque d'incohérence sur une
 * logique sensible (détection du hub, extraction du slug tenant).
 *
 * Pendant du `host-resolver.ts` côté API : mêmes hôtes hub, mêmes suffixes
 * tenant. Un `VITE_PUBLIC_ROOT_DOMAIN` injecté au build ajoute un domaine
 * racine additionnel (déploiement de test / preview servi ailleurs que sur
 * `crayea.fr`, ex. un domaine gratuit `crayea.us.kg`) — le pendant front de
 * `PUBLIC_ROOT_DOMAIN` côté API. Vide en prod normale.
 */

import {
  construireHotesHub,
  construireSuffixesTenant,
  HOTES_HUB_DEV,
  SUFFIXE_TENANT_DEV,
} from "@champ-erp/config/tenant-host";

/**
 * Domaine racine additionnel (build-time). Vide en prod normale. Passé brut aux
 * helpers de `@champ-erp/config`, qui le normalisent (trim/lowercase/vide→ignoré).
 */
const EXTRA_ROOT = import.meta.env["VITE_PUBLIC_ROOT_DOMAIN"];

/**
 * Hôtes hub à correspondance EXACTE (dev + prod + éventuel domaine de test).
 * Dev et prod sont fusionnés dans un même Set : côté navigateur il n'y a pas de
 * flag prod/dev, on se fie au hostname réel (l'appartenance au Set est un test
 * d'égalité, insensible à l'ordre).
 */
const HUB_HOSTS = new Set<string>([...HOTES_HUB_DEV, ...construireHotesHub(EXTRA_ROOT)]);

/** Suffixes de sous-domaine tenant en prod (+ éventuel domaine de test). */
const PROD_TENANT_SUFFIXES: readonly string[] = construireSuffixesTenant(EXTRA_ROOT);

/** Suffixe tenant en dev local. */
const DEV_TENANT_SUFFIX = SUFFIXE_TENANT_DEV;

/** `true` si le host est un hub (login / signup / choix de domaine). */
export function isHubHost(hostname: string): boolean {
  return HUB_HOSTS.has(hostname.toLowerCase());
}

/**
 * Slug du tenant déduit du host (`champagne-dupont.crayea.fr` →
 * `champagne-dupont`), ou `null` si le host n'est pas un sous-domaine tenant
 * (hub, host inconnu, ou label `app`).
 */
export function tenantSlugFromHost(hostname: string): string | null {
  const host = hostname.toLowerCase();
  if (isHubHost(host)) return null;

  for (const suffix of PROD_TENANT_SUFFIXES) {
    if (host.endsWith(suffix)) {
      const slug = host.slice(0, -suffix.length);
      return slug.length > 0 && slug !== "app" ? slug : null;
    }
  }
  if (host.endsWith(DEV_TENANT_SUFFIX)) {
    const slug = host.slice(0, -DEV_TENANT_SUFFIX.length);
    return slug.length > 0 && slug !== "app" ? slug : null;
  }
  return null;
}

/** `true` si on est sur un sous-domaine tenant (`<slug>.<racine>`). */
export function isTenantHost(hostname: string): boolean {
  return tenantSlugFromHost(hostname) !== null;
}

/**
 * Origin du hub correspondant au host courant (`https://app.<racine>` en prod,
 * `http://localhost:<port>` en dev), ou `null` si on est DÉJÀ sur un hub.
 *
 * Sert à renvoyer un utilisateur qui atterrit par erreur sur un sous-domaine
 * tenant (bookmark, lien partagé, redirect 401) vers le bon hub — sans jamais
 * reprendre le hostname d'une source non fiable (on ne dérive que du suffixe
 * racine connu). En dev, on préserve le port réel du navigateur.
 */
export function hubOriginForCurrentHost(loc: {
  hostname: string;
  port: string;
  protocol: string;
}): string | null {
  const host = loc.hostname.toLowerCase();
  if (isHubHost(host)) return null;

  if (host.endsWith(DEV_TENANT_SUFFIX)) {
    const portSuffix = loc.port ? `:${loc.port}` : "";
    return `${loc.protocol}//localhost${portSuffix}`;
  }
  for (const suffix of PROD_TENANT_SUFFIXES) {
    if (host.endsWith(suffix)) return `https://app${suffix}`;
  }
  return null;
}
